import React, { useEffect, useRef, useState } from 'react';
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { useTranslation } from '../../i18n';
import { useToast } from '../../contexts/ToastContext';
import { clickableProps } from '../../utils/a11y';
import { formatDate } from '../../utils/format';
import {
  addDays,
  describeDue,
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
  toLocalDateString,
} from '../TodoDetailView/todoDetailFormatters';
import Icon from '../Icon';
import './HomeToday.css';

export interface DueTodayItem {
  todo: Todo;
  subGoal: SubGoal;
  goal: Goal;
}

/** At most this many overdue todos start open; more start folded (T-109, same as native). */
export const OVERDUE_OPEN_LIMIT = 3;
/** Rows a group shows before "Show all". */
export const GROUP_PREVIEW = 5;

/** The user's open/fold choice for the Overdue group, kept for this page session. */
let overdueChoice: boolean | null = null;
/** For tests: forget the choice. */
export const resetOverdueChoice = () => { overdueChoice = null; };

/**
 * Open todos due today or earlier, the most overdue first (same rules as the native
 * Home "Today" section, T-076 / T-087).
 */
export const dueTodayTodos = (goals: Goal[], today: string): DueTodayItem[] => {
  const items: DueTodayItem[] = [];
  goals.forEach((goal) => {
    (goal.subGoals ?? []).forEach((subGoal) => {
      (subGoal.todos ?? []).forEach((todo) => {
        if (todo.completed || !todo.dueDate) return;
        const { state } = describeDue(todo.dueDate, today);
        if (state === 'overdue' || state === 'today') items.push({ todo, subGoal, goal });
      });
    });
  });
  return items.sort((a, b) => (a.todo.dueDate as string).localeCompare(b.todo.dueDate as string));
};

/** The reminder moved to [day] at the same wall-clock time in the account's zone. */
export const shiftReminder = (reminderAt: string | null | undefined, day: string): string | null => {
  if (!reminderAt) return null;
  const wall = toDateTimeLocalValue(reminderAt);
  if (wall.length < 16) return reminderAt;
  return fromDateTimeLocalValue(`${day}${wall.slice(10)}`) ?? reminderAt;
};

type Move = (item: DueTodayItem, day: string) => void;

/** "⋯" on a row: Today (overdue only), Tomorrow, Next week, or a picked date. */
const RescheduleMenu: React.FC<{ item: DueTodayItem; today: string; onMove: Move }> = ({ item, today, onMove }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const overdue = item.todo.dueDate !== today;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) setPicking(false);
    else root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const choose = (day: string) => {
    setOpen(false);
    onMove(item, day);
  };

  return (
    <div
      className="home-today-menu"
      ref={root}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        className="home-today-more"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('home.today.moreFor', { title: item.todo.title })}
        title={t('due.reschedule')}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" />
      </button>
      {open && (
        <div className="home-today-menu__list" role="menu" aria-label={t('due.reschedule')}>
          {overdue && (
            <button type="button" role="menuitem" onClick={() => choose(today)}>
              <Icon name="sun" /> {t('due.today')}
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => choose(addDays(today, 1))}>
            <Icon name="chevronRight" /> {t('due.tomorrow')}
          </button>
          <button type="button" role="menuitem" onClick={() => choose(addDays(today, 7))}>
            <Icon name="calendar" /> {t('due.nextWeek')}
          </button>
          {picking ? (
            <input
              type="date"
              className="home-today-menu__date"
              aria-label={t('due.pickDate')}
              defaultValue={item.todo.dueDate ?? today}
              autoFocus
              onChange={(e) => { if (e.target.value) choose(e.target.value); }}
            />
          ) : (
            <button type="button" role="menuitem" onClick={() => setPicking(true)}>
              <Icon name="edit" /> {t('due.pickDate')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ item: DueTodayItem; today: string; onMove?: Move }> = ({ item, today, onMove }) => {
  const { t } = useTranslation();
  const due = describeDue(item.todo.dueDate as string, today);
  const open = () => {
    window.location.hash = `#/subgoals/${item.subGoal.id}/todos/${item.todo.id}`;
  };
  return (
    <li className="home-today-row">
      <div className="home-today-row__main" {...clickableProps(open, 'link')}>
        <div className="home-today-text">
          <bdi className="home-today-title">{item.todo.title}</bdi>
          <bdi className="home-today-path">{`${item.goal.title} › ${item.subGoal.title}`}</bdi>
        </div>
        {/* Inside the Overdue group the chip says how late; today's need none. */}
        {due.state === 'overdue' && (
          <span className="home-today-overdue">{t('due.overdue', { days: due.days })}</span>
        )}
      </div>
      {onMove && <RescheduleMenu item={item} today={today} onMove={onMove} />}
    </li>
  );
};

const Group: React.FC<{
  items: DueTodayItem[];
  today: string;
  onMove?: Move;
  header?: React.ReactNode;
  hidden?: boolean;
}> = ({ items, today, onMove, header, hidden = false }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const visible = all ? items : items.slice(0, GROUP_PREVIEW);
  return (
    <div className="home-today-group">
      {header}
      {!hidden && (
        <>
          <ul className="home-today-list">
            {visible.map((item) => <Row key={item.todo.id} item={item} today={today} onMove={onMove} />)}
          </ul>
          {items.length > GROUP_PREVIEW && (
            <button type="button" className="home-today-showall" onClick={() => setAll((v) => !v)}>
              {all ? t('home.today.showLess') : t('home.today.showAll', { count: items.length })}
            </button>
          )}
        </>
      )}
    </div>
  );
};

interface HomeTodayProps {
  goals: Goal[];
  /** Saves a todo change; it rejects (after its own error toast) when the save failed. */
  onUpdateTodo?: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void> | void;
  /** Todos already shown above in "Continue" (T-153), left out here. */
  excludeIds?: ReadonlySet<string>;
}

/**
 * "Today" on the home page (T-087, regrouped in T-109 like native T-107): overdue todos in a
 * folding group above today's open list, each capped with "Show all". A row opens its todo;
 * its "⋯" moves it to another day with Undo, the reminder keeping its time.
 */
const HomeToday: React.FC<HomeTodayProps> = ({ goals, onUpdateTodo, excludeIds }) => {
  const { t, language } = useTranslation();
  const toast = useToast();
  const today = toLocalDateString();
  const items = dueTodayTodos(goals, today).filter((item) => !excludeIds?.has(item.todo.id));
  const overdue = items.filter((item) => item.todo.dueDate !== today);
  const dueToday = items.filter((item) => item.todo.dueDate === today);
  const [overdueOpen, setOverdueOpen] = useState<boolean>(overdueChoice ?? overdue.length <= OVERDUE_OPEN_LIMIT);
  if (items.length === 0) return null;

  const move: Move | undefined = onUpdateTodo && (async (item, day) => {
    const { todo, subGoal } = item;
    if (todo.dueDate === day) return;
    const before = { dueDate: todo.dueDate ?? null, reminderAt: todo.reminderAt ?? null };
    try {
      await onUpdateTodo(subGoal.id, todo.id, { dueDate: day, reminderAt: shiftReminder(todo.reminderAt, day) });
    } catch {
      return; // The save already showed its error.
    }
    const label = day === today
      ? t('due.today')
      : day === addDays(today, 1) ? t('due.tomorrow') : formatDate(day, language);
    toast.success(t('due.newDate', { date: label }), {
      actions: [{
        label: t('common.undo'),
        onClick: () => { Promise.resolve(onUpdateTodo(subGoal.id, todo.id, before)).catch(() => undefined); },
      }],
    });
  });

  const toggleOverdue = () => {
    overdueChoice = !overdueOpen;
    setOverdueOpen(!overdueOpen);
  };

  return (
    <section className="home-today" aria-labelledby="home-today-title">
      <div className="home-today-header">
        <Icon name="calendar" className="home-today-icon" />
        <h3 id="home-today-title">{t('due.today')}</h3>
        {dueToday.length > 0 && <span className="home-today-count">{dueToday.length}</span>}
      </div>
      {overdue.length > 0 && (
        <Group
          items={overdue}
          today={today}
          onMove={move}
          hidden={!overdueOpen}
          header={(
            <button
              type="button"
              className="home-today-overdue-header"
              aria-expanded={overdueOpen}
              onClick={toggleOverdue}
            >
              <Icon name="warning" />
              <span>{`${t('home.today.overdue')} (${overdue.length})`}</span>
              <Icon name={overdueOpen ? 'chevronUp' : 'chevronDown'} className="home-today-chevron" />
            </button>
          )}
        />
      )}
      {dueToday.length > 0 && <Group items={dueToday} today={today} onMove={move} />}
    </section>
  );
};

export default HomeToday;
